import type { QueryClient } from '@tanstack/react-query';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { projectsService } from '@/features/project/api/projects';
import type { User } from '@/types/user';

const FALLBACK_DESTINATION = '/';

interface ResolveOptions {
  queryClient: QueryClient;
  defaultProjectId?: string | null;
  redirectUrl?: string | null;
}

/**
 * Where the user lands right after a cold sign-in / sign-up / verify.
 *
 * Priority:
 *   1. caller-provided `redirectUrl` (?redirect= search param) — user came from a deep link
 *   2. server-issued `defaultProjectId` (auto-created starter project for brand-new accounts)
 *   3. solo-project shortcut: `has_completed_renders === false` AND exactly one project →
 *      open that project. Skips the dashboard tile-with-one-card flash on the first session
 *      after onboarding. Mirrors Apple/Android behaviour.
 *   4. `/` resolver fallback (also if the projects fetch fails — don't block login on a network blip).
 *      The root resolver redirects to the last-opened / first building, or shows the create state.
 *
 * Cold-login only: callers wire this into login / register / verify-email / reset-password.
 * Warm session-restore stays on whatever screen the user was on.
 */
export async function resolvePostLoginDestination(
  user: User | null,
  options: ResolveOptions
): Promise<string> {
  if (options.redirectUrl) return options.redirectUrl;
  if (options.defaultProjectId) return `/project/${options.defaultProjectId}`;
  if (!user || user.has_completed_renders !== false) return FALLBACK_DESTINATION;

  try {
    const response = await options.queryClient.fetchQuery({
      queryKey: cacheKeys.projects.list(),
      queryFn: () => projectsService.list({ sort: '-created_at' }),
    });
    const [solo] = response.data;
    if (response.data.length === 1 && solo) {
      return `/project/${solo.id}`;
    }
  } catch {
    // graceful — fall through to dashboard
  }
  return FALLBACK_DESTINATION;
}
